import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
    Loader2, ArrowLeft, Plus, Trash2, Copy, Save, X, ChevronDown, ChevronUp,
    AlertTriangle, Package, Repeat, Lock, Info,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import MoneyTabs from "@/components/coach/MoneyTabs";
import SendPricingSheetCard from "@/components/coach/SendPricingSheetCard";
import PackExpiryBackfillCard from "@/components/coach/PackExpiryBackfillCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TAX_RATE = 0.103; // mirrors backend default; we display this in calc previews

const fmtMoney = (cents) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((cents || 0) / 100);

const centsToDollarsStr = (cents) => ((cents || 0) / 100).toFixed(2);


/**
 * MoneyInput — uncontrolled-feeling money field that fixes the "I type 47
 * but only see 4.00" bug. We keep raw text in local state so the user can
 * freely type, backspace, and edit. We push parsed cents to the parent
 * after every valid keystroke. On blur we reformat to "X.XX" so the field
 * always renders cleanly when not focused.
 *
 * Accepts integer dollar input ("47"), decimals ("47.5", "47.50"), and
 * empty string (treated as 0). Rejects characters that aren't digits or
 * a single decimal point.
 */
function MoneyInput({ valueCents, onChangeCents, testid, className, ...rest }) {
    const [text, setText] = useState(centsToDollarsStr(valueCents));

    // Sync FROM parent when the cents value changes externally (e.g. cancel
    // edit, parent reset) and the change isn't already reflected in `text`.
    // Comparing on parsed value avoids overwriting "47" with "47.00" while
    // the user is mid-typing.
    useEffect(() => {
        const parsed = parseFloat(text);
        const cents = Number.isFinite(parsed) ? Math.round(parsed * 100) : 0;
        if (cents !== valueCents) {
            setText(centsToDollarsStr(valueCents));
        }
    }, [valueCents, text]);

    return (
        <input
            type="text"
            inputMode="decimal"
            value={text}
            onChange={(e) => {
                const raw = e.target.value;
                // Allow empty, "X", "X.", "X.YY". Up to 2 decimal places.
                if (raw !== "" && !/^\d*\.?\d{0,2}$/.test(raw)) return;
                setText(raw);
                const v = parseFloat(raw);
                onChangeCents(Number.isFinite(v) && v >= 0 ? Math.round(v * 100) : 0);
            }}
            onFocus={(e) => {
                // Select-all on focus so the user can immediately type a
                // replacement number without manually clearing first. This
                // is the single biggest UX win for "I want to type 47" flow.
                e.target.select();
            }}
            onBlur={() => {
                // Reformat to 2 decimals on blur so the resting state always
                // looks clean. If user typed "47" → blur shows "47.00".
                setText(centsToDollarsStr(valueCents));
            }}
            data-testid={testid}
            className={className}
            {...rest}
        />
    );
}

/**
 * Pricing Blocks — owner-only editor.
 *
 * A "block" bundles an optional subscription (price + cadence + commitment
 * + recurring/upfront toggles) and a list of session packages. Coach edits
 * here flow into the lead-detail charge UI in Phase 2; for now this is a
 * pure config tool. No charges depend on these rows yet.
 */
export default function PricingBlocks() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [blocks, setBlocks] = useState([]);
    const [busy, setBusy] = useState(true);
    const [drafts, setDrafts] = useState({}); // block_id -> draft block (null = not editing)
    const [savingId, setSavingId] = useState(null);
    const [creating, setCreating] = useState(false);
    const [expanded, setExpanded] = useState({}); // block_id -> bool

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/", { replace: true });
            return;
        }
        if (user.role !== "owner" && !user.permissions?.manage_pricing) {
            toast.error("You don't have access to pricing blocks");
            setTimeout(() => navigate("/coach", { replace: true }), 1200);
        }
    }, [loading, user, navigate]);

    const load = useCallback(async () => {
        setBusy(true);
        try {
            const r = await axios.get(`${API}/coach/pricing-blocks`, { withCredentials: true });
            setBlocks(r.data?.blocks || []);
        } catch (e) {
            toast.error("Couldn't load pricing blocks", { description: e?.response?.data?.detail });
        } finally {
            setBusy(false);
        }
    }, []);

    useEffect(() => {
        if (user?.role === "owner" || user?.permissions?.manage_pricing) load();
    }, [user, load]);

    const startEdit = (block) => {
        setDrafts((d) => ({ ...d, [block.block_id]: deepClone(block) }));
        setExpanded((e) => ({ ...e, [block.block_id]: true }));
    };

    const cancelEdit = (block_id) => {
        setDrafts((d) => {
            const next = { ...d };
            delete next[block_id];
            return next;
        });
    };

    const updateDraft = (block_id, patch) => {
        setDrafts((d) => ({
            ...d,
            [block_id]: typeof patch === "function" ? patch(d[block_id]) : { ...d[block_id], ...patch },
        }));
    };

    const save = async (block_id) => {
        const draft = drafts[block_id];
        if (!draft) return;
        if (!draft.name?.trim()) {
            toast.error("Block name is required");
            return;
        }
        setSavingId(block_id);
        try {
            const payload = serializeForApi(draft);
            const isNew = String(block_id).startsWith("new_");
            const r = isNew
                ? await axios.post(`${API}/coach/pricing-blocks`, payload, { withCredentials: true })
                : await axios.patch(`${API}/coach/pricing-blocks/${block_id}`, payload, { withCredentials: true });
            toast.success(isNew ? "Block created" : "Saved", { description: r.data?.name });
            cancelEdit(block_id);
            await load();
        } catch (e) {
            toast.error("Couldn't save", { description: e?.response?.data?.detail || e?.message });
        } finally {
            setSavingId(null);
        }
    };

    const onCreate = () => {
        const tmp_id = `new_${Date.now()}`;
        const blank = {
            block_id: tmp_id,
            name: "New Block",
            description: "",
            sort_order: 999,
            active: true,
            is_fallback: false,
            rollover_sessions: true,
            show_in_pricing_email: false,
            subscription: null,
            packages: [],
            created_at: "",
            updated_at: "",
        };
        // Inject a temporary row at the top of the editor and start editing it.
        setBlocks((b) => [...b, blank]);
        setDrafts((d) => ({ ...d, [tmp_id]: deepClone(blank) }));
        setExpanded((e) => ({ ...e, [tmp_id]: true }));
        setCreating(true);
    };

    const onDuplicate = async (block_id) => {
        try {
            const r = await axios.post(`${API}/coach/pricing-blocks/${block_id}/duplicate`, {}, { withCredentials: true });
            toast.success("Duplicated", { description: r.data?.name });
            await load();
            setExpanded((e) => ({ ...e, [r.data?.block_id]: true }));
        } catch (e) {
            toast.error("Couldn't duplicate", { description: e?.response?.data?.detail });
        }
    };

    const onDelete = async (block_id, name) => {
        if (!window.confirm(`Deactivate "${name}"? It'll be hidden from billing UIs but kept for audit trails. You can re-activate later.`)) return;
        try {
            await axios.delete(`${API}/coach/pricing-blocks/${block_id}`, { withCredentials: true });
            toast.success("Block deactivated");
            await load();
        } catch (e) {
            toast.error("Couldn't delete", { description: e?.response?.data?.detail });
        }
    };

    if (loading || busy) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-black">
                <Loader2 className="w-6 h-6 animate-spin text-brand-red" />
            </div>
        );
    }
    if (user?.role !== "owner" && !user?.permissions?.manage_pricing) return null;

    return (
        <div className="min-h-screen bg-black text-white pb-24">
            <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-6">
                <MoneyTabs />
                <button
                    type="button"
                    onClick={() => navigate("/coach")}
                    className="text-zinc-400 hover:text-white text-[10px] tracking-[0.25em] uppercase inline-flex items-center gap-1.5 mb-6"
                    data-testid="pricing-blocks-back"
                >
                    <ArrowLeft className="w-3.5 h-3.5" /> Back to console
                </button>

                <div className="flex items-end justify-between mb-2">
                    <div>
                        <div className="text-[10px] tracking-[0.3em] uppercase text-brand-red mb-1">Owner controls</div>
                        <h1 className="font-display text-3xl sm:text-4xl tracking-wider uppercase">Pricing Blocks</h1>
                    </div>
                    <button
                        type="button"
                        onClick={onCreate}
                        disabled={creating}
                        data-testid="pricing-blocks-new"
                        className="btn-tactical text-xs whitespace-nowrap"
                    >
                        <Plus className="w-3.5 h-3.5" /> New Block
                    </button>
                </div>
                <p className="text-sm text-zinc-400 mt-2 max-w-3xl">
                    Bundles of pricing the gym sells. Each block can have an optional subscription and any number of session packages.
                    Edits here will drive the charge UI in Phase 2 — nothing here affects existing customers yet.
                </p>

                <div className="mt-6">
                    <SendPricingSheetCard />
                </div>

                <div className="mt-6">
                    <PackExpiryBackfillCard />
                </div>

                {/* Sachin 2026-03-06 — silent discount audit. If any block
                    carries a coach-editable `coupon` config, that coupon
                    auto-applies on every subscribe from that block with
                    no coach UI interaction. Loud banner so it stops being
                    a silent revenue leak. */}
                {(() => {
                    const risky = blocks.filter((b) => b.coupon && b.coupon.enabled);
                    if (risky.length === 0) return null;
                    return (
                        <div
                            data-testid="pricing-blocks-coupon-warning"
                            className="mt-6 border border-amber-600/60 bg-amber-950/30 rounded-lg p-4"
                            role="alert"
                        >
                            <div className="flex items-start gap-3">
                                <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                                <div className="min-w-0 flex-1">
                                    <div className="text-[10px] tracking-[0.25em] uppercase text-amber-300 font-semibold mb-1.5">
                                        Auto-applied discounts detected
                                    </div>
                                    <p className="text-sm text-zinc-200 leading-relaxed">
                                        The following {risky.length === 1 ? "block has a saved coupon" : `${risky.length} blocks have saved coupons`}. Any subscription started from {risky.length === 1 ? "this block" : "these blocks"} will automatically apply the coupon — the coach will still see it in the confirmation dialog, but no discount input is required.
                                    </p>
                                    <ul className="mt-3 space-y-1.5">
                                        {risky.map((b) => {
                                            const c = b.coupon || {};
                                            const value = c.mode === "percent"
                                                ? `${c.value ?? 0}% off`
                                                : `$${((c.value_cents ?? 0) / 100).toFixed(2)} off`;
                                            const dur = c.duration === "forever"
                                                ? "FOREVER"
                                                : c.duration === "repeating"
                                                    ? `${c.periods ?? "?"} periods`
                                                    : "once";
                                            return (
                                                <li
                                                    key={b.block_id}
                                                    data-testid={`pricing-blocks-coupon-row-${b.block_id}`}
                                                    className="flex items-center flex-wrap gap-2 text-xs"
                                                >
                                                    <span className="font-semibold text-white">{b.name}</span>
                                                    <span className="font-mono text-amber-200">
                                                        {value} · <span className={dur === "FOREVER" ? "font-bold" : ""}>{dur}</span>
                                                    </span>
                                                    {c.reason && (
                                                        <span className="text-zinc-400">— {c.reason}</span>
                                                    )}
                                                </li>
                                            );
                                        })}
                                    </ul>
                                    <p className="text-[11px] text-zinc-400 mt-3 leading-relaxed">
                                        No admin UI to edit these lives here yet — the config is set directly in the pricing_blocks DB. If any of the above wasn&apos;t intended, contact engineering to remove it.
                                    </p>
                                </div>
                            </div>
                        </div>
                    );
                })()}

                <div className="mt-8 space-y-4">
                    {blocks.length === 0 ? (
                        <div className="border border-zinc-800 rounded-xl p-8 text-center text-zinc-400 text-sm">
                            No blocks yet. Click <b>New Block</b> to create one.
                        </div>
                    ) : (
                        blocks
                            .slice()
                            .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.name.localeCompare(b.name))
                            .map((block) => (
                                <BlockCard
                                    key={block.block_id}
                                    block={block}
                                    draft={drafts[block.block_id]}
                                    expanded={!!expanded[block.block_id]}
                                    onToggleExpand={() => setExpanded((e) => ({ ...e, [block.block_id]: !e[block.block_id] }))}
                                    onStartEdit={() => startEdit(block)}
                                    onCancelEdit={() => {
                                        // Clean up "new_" temp rows on cancel.
                                        if (String(block.block_id).startsWith("new_")) {
                                            setBlocks((b) => b.filter((x) => x.block_id !== block.block_id));
                                            setCreating(false);
                                        }
                                        cancelEdit(block.block_id);
                                    }}
                                    onUpdateDraft={(patch) => updateDraft(block.block_id, patch)}
                                    onSave={() => save(block.block_id)}
                                    onDuplicate={() => onDuplicate(block.block_id)}
                                    onDelete={() => onDelete(block.block_id, block.name)}
                                    saving={savingId === block.block_id}
                                />
                            ))
                    )}
                </div>
            </div>
        </div>
    );
}


// =============== Block card ===============

function BlockCard({
    block, draft, expanded,
    onToggleExpand, onStartEdit, onCancelEdit, onUpdateDraft, onSave,
    onDuplicate, onDelete, saving,
}) {
    const editing = !!draft;
    const data = editing ? draft : block;
    const subEnabled = !!data.subscription?.enabled;
    const isNew = String(block.block_id).startsWith("new_");

    return (
        <div
            data-testid={`pricing-block-${block.block_id}`}
            className={`border rounded-xl bg-zinc-950 ${
                editing ? "border-brand-red" : data.active ? "border-zinc-800" : "border-zinc-900 opacity-70"
            }`}
        >
            {/* Header */}
            <div className="px-5 py-4 flex items-center justify-between gap-3">
                <button
                    type="button"
                    onClick={onToggleExpand}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                    data-testid={`pricing-block-toggle-${block.block_id}`}
                >
                    {expanded ? (
                        <ChevronUp className="w-4 h-4 text-zinc-400 shrink-0" />
                    ) : (
                        <ChevronDown className="w-4 h-4 text-zinc-400 shrink-0" />
                    )}
                    <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-display text-lg sm:text-xl tracking-wide uppercase truncate">
                                {data.name || "Untitled block"}
                            </span>
                            {data.is_fallback && (
                                <span className="inline-flex items-center gap-1 text-[9px] tracking-[0.25em] uppercase px-1.5 py-0.5 bg-brand-red/15 text-brand-red border border-brand-red/40 rounded">
                                    <Lock className="w-2.5 h-2.5" /> Fallback
                                </span>
                            )}
                            {!data.active && (
                                <span className="text-[9px] tracking-[0.25em] uppercase px-1.5 py-0.5 bg-zinc-800 text-zinc-400 rounded">
                                    Inactive
                                </span>
                            )}
                        </div>
                        <div className="text-xs text-zinc-400 mt-1 flex items-center gap-3 flex-wrap">
                            {subEnabled && (
                                <span className="inline-flex items-center gap-1">
                                    <Repeat className="w-3 h-3" />
                                    {fmtMoney(data.subscription.price_cents)}/{intervalShort(data.subscription.interval)} ·{" "}
                                    {data.subscription.commitment_count}-{intervalUnit(data.subscription.interval)} commit
                                </span>
                            )}
                            <span className="inline-flex items-center gap-1">
                                <Package className="w-3 h-3" />
                                {data.packages.length} package{data.packages.length === 1 ? "" : "s"}
                            </span>
                        </div>
                    </div>
                </button>

                {/* Action buttons (collapsed view only) */}
                {!editing && (
                    <div className="flex items-center gap-1 shrink-0">
                        <IconBtn onClick={onStartEdit} title="Edit" testid={`pricing-block-edit-${block.block_id}`}>
                            <Save className="w-3.5 h-3.5" />
                        </IconBtn>
                        <IconBtn onClick={onDuplicate} title="Duplicate" testid={`pricing-block-duplicate-${block.block_id}`}>
                            <Copy className="w-3.5 h-3.5" />
                        </IconBtn>
                        {data.active && (
                            <IconBtn onClick={onDelete} title="Deactivate" danger testid={`pricing-block-delete-${block.block_id}`}>
                                <Trash2 className="w-3.5 h-3.5" />
                            </IconBtn>
                        )}
                    </div>
                )}
            </div>

            {/* Body */}
            {expanded && (
                <div className="border-t border-zinc-800 p-5 space-y-5">
                    {editing ? (
                        <BlockEditor data={data} onUpdate={onUpdateDraft} isNew={isNew} />
                    ) : (
                        <BlockReadOnly data={data} />
                    )}

                    {editing && (
                        <div className="border-t border-zinc-800 pt-4 flex items-center justify-end gap-2">
                            <button
                                type="button"
                                onClick={onCancelEdit}
                                disabled={saving}
                                className="btn-ghost-tactical text-xs"
                                data-testid={`pricing-block-cancel-${block.block_id}`}
                            >
                                <X className="w-3.5 h-3.5" /> Cancel
                            </button>
                            <button
                                type="button"
                                onClick={onSave}
                                disabled={saving}
                                className="btn-tactical text-xs"
                                data-testid={`pricing-block-save-${block.block_id}`}
                            >
                                {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                                {isNew ? "Create block" : "Save changes"}
                            </button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}


// =============== Editor ===============

function BlockEditor({ data, onUpdate, isNew }) {
    const sub = data.subscription;
    const subEnabled = !!sub?.enabled;

    const setSub = (patch) => onUpdate((d) => ({
        ...d,
        subscription: { ...(d.subscription || defaultSub()), ...patch },
    }));

    const enableSub = (on) => onUpdate((d) => ({
        ...d,
        subscription: on ? { ...(d.subscription || defaultSub()), enabled: true } : null,
    }));

    const setReactivationFee = (cents) => onUpdate({ reactivation_fee_cents: cents });

    const addPackage = () => onUpdate((d) => ({
        ...d,
        packages: [...d.packages, {
            package_id: null,
            sessions: 1,
            per_session_cents: 5000,
            manual_per_session_cents: null,  // null = same as auto price
            auto_renew_default: true,
            label: "",
        }],
    }));

    const removePackage = (idx) => onUpdate((d) => ({
        ...d,
        packages: d.packages.filter((_, i) => i !== idx),
    }));

    const updatePkg = (idx, patch) => onUpdate((d) => ({
        ...d,
        packages: d.packages.map((p, i) => (i === idx ? { ...p, ...patch } : p)),
    }));

    return (
        <div className="space-y-5">
            {/* Top metadata */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Block name">
                    <input
                        data-testid="block-name-input"
                        value={data.name}
                        onChange={(e) => onUpdate({ name: e.target.value })}
                        className={inp}
                        placeholder="e.g. 3-Week Commitment"
                    />
                </Field>
                <Field label="Description (optional)">
                    <input
                        data-testid="block-description-input"
                        value={data.description || ""}
                        onChange={(e) => onUpdate({ description: e.target.value })}
                        className={inp}
                        placeholder="Short note shown on parent pricing page"
                    />
                </Field>
            </div>

            <div className="flex flex-wrap gap-4">
                <Toggle
                    on={data.active}
                    onChange={(v) => onUpdate({ active: v })}
                    label="Active"
                    hint="Inactive blocks are hidden from billing UIs."
                    testid="block-active-toggle"
                />
                <Toggle
                    on={data.rollover_sessions}
                    onChange={(v) => onUpdate({ rollover_sessions: v })}
                    label="Rollover sessions on renewal"
                    hint="Unused pack sessions move into the next pack on renewal."
                    testid="block-rollover-toggle"
                />
                <Toggle
                    on={data.is_fallback}
                    onChange={(v) => onUpdate({ is_fallback: v })}
                    label="Fallback rollover block"
                    hint="If checked, expiring subs that don't take action auto-roll into THIS block's subscription. Only one block can be the fallback."
                    danger
                    testid="block-fallback-toggle"
                />
                <Toggle
                    on={data.show_in_pricing_email}
                    onChange={(v) => onUpdate({ show_in_pricing_email: v })}
                    label="Show in pricing email"
                    hint="When ON, this block is included when a coach clicks 'Send Pricing' on a lead. Turn ON only the block(s) you want the parent to see. If NO block is toggled ON, the email falls back to every 12-week commitment block."
                    testid="block-show-in-email-toggle"
                />
            </div>

            {/* Subscription section */}
            <div className="border border-zinc-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                        <Repeat className="w-4 h-4 text-brand-red" />
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-semibold">Subscription</div>
                    </div>
                    <Toggle
                        on={subEnabled}
                        onChange={enableSub}
                        label={subEnabled ? "Has subscription" : "No subscription"}
                        testid="block-sub-enabled-toggle"
                        compact
                    />
                </div>

                {subEnabled && sub && (
                    <div className="space-y-4">
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                            <Field label="Price ($)">
                                <MoneyInput
                                    testid="block-sub-price-input"
                                    valueCents={sub.price_cents}
                                    onChangeCents={(c) => setSub({ price_cents: c })}
                                    className={inp}
                                />
                            </Field>
                            <Field label="Cadence">
                                <select
                                    data-testid="block-sub-interval-select"
                                    value={sub.interval}
                                    onChange={(e) => setSub({ interval: e.target.value })}
                                    className={inp}
                                >
                                    <option value="daily">Daily</option>
                                    <option value="weekly">Weekly</option>
                                    <option value="monthly">Monthly</option>
                                </select>
                            </Field>
                            <Field label={`Commitment (${intervalUnit(sub.interval)}s)`}>
                                <input
                                    data-testid="block-sub-commitment-input"
                                    type="number"
                                    min="1"
                                    step="1"
                                    value={sub.commitment_count}
                                    onChange={(e) => setSub({ commitment_count: Math.max(1, parseInt(e.target.value) || 1) })}
                                    className={inp}
                                />
                            </Field>
                        </div>

                        <div className="text-[11px] tracking-[0.2em] uppercase text-zinc-400">Billing options</div>
                        <div className="flex flex-wrap gap-4">
                            <Toggle
                                on={sub.allow_recurring}
                                onChange={(v) => setSub({ allow_recurring: v })}
                                label="Auto-bill each period"
                                hint={`${fmtMoney(sub.price_cents)} / ${intervalShort(sub.interval)} for ${sub.commitment_count} ${intervalUnit(sub.interval)}${sub.commitment_count === 1 ? "" : "s"}`}
                                testid="block-sub-recurring-toggle"
                            />
                            <Toggle
                                on={sub.allow_upfront}
                                onChange={(v) => setSub({ allow_upfront: v })}
                                label="Pay upfront (one-time)"
                                hint={`${fmtMoney(sub.price_cents * sub.commitment_count)} charged once · no recurring`}
                                testid="block-sub-upfront-toggle"
                            />
                        </div>

                        <SubPreview sub={sub} />
                    </div>
                )}
            </div>

            {/* Packages section */}
            <div className="border border-zinc-800 rounded-lg p-4 space-y-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <Package className="w-4 h-4 text-brand-red" />
                        <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-semibold">
                            Session packages
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={addPackage}
                        className="btn-ghost-tactical text-[10px] tracking-[0.2em] uppercase"
                        data-testid="block-add-package"
                    >
                        <Plus className="w-3 h-3" /> Add package
                    </button>
                </div>
                {data.packages.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic px-1">No packages yet — click &ldquo;Add package&rdquo; to create one.</div>
                ) : (
                    <div className="space-y-2">
                        {data.packages.map((p, idx) => (
                            <PackageRow
                                key={p.package_id || `idx_${idx}`}
                                pkg={p}
                                onChange={(patch) => updatePkg(idx, patch)}
                                onRemove={() => removePackage(idx)}
                                idx={idx}
                            />
                        ))}
                    </div>
                )}
            </div>

            {/* Reactivation fee section */}
            <ReactivationFeeBlock
                valueCents={data.reactivation_fee_cents || 0}
                onChange={setReactivationFee}
            />

            {!isNew && (
                <p className="text-[11px] text-zinc-500 inline-flex items-center gap-1.5">
                    <Info className="w-3 h-3" />
                    Saving updates the block in place. In-flight customer subscriptions and packs continue at their original price.
                </p>
            )}
        </div>
    );
}


// =============== Reactivation fee section helper ===============
//
// Used inside the editor: a per-block "Reactivation fee" field that
// applies when an athlete has an active sub but their last pack expired
// with unused sessions. Charged on top of the next pack purchase.
function ReactivationFeeBlock({ valueCents, onChange }) {
    return (
        <div className="border border-zinc-800 rounded-lg p-4">
            <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-300 font-semibold">
                    Reactivation fee
                </div>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3 max-w-prose">
                Added on top of the NEXT pack purchase when this athlete has an active subscription but their previous pack expired with sessions unused. Set to <span className="text-zinc-300">$0</span> to disable.
            </p>
            <div className="w-32">
                <MoneyInput
                    valueCents={valueCents}
                    onChangeCents={onChange}
                    className={inp}
                    testid="block-reactivation-fee"
                />
            </div>
        </div>
    );
}


function PackageRow({ pkg, onChange, onRemove, idx }) {
    const subtotal = pkg.sessions * pkg.per_session_cents;
    const tax = Math.round(subtotal * TAX_RATE);
    const total = subtotal + tax;

    // Manual pricing — coach can optionally set a different per-session price
    // when this pack is sold WITHOUT auto-renew. Null means "use auto price".
    const hasManual = pkg.manual_per_session_cents != null;
    const manualSubtotal = hasManual ? pkg.sessions * pkg.manual_per_session_cents : null;
    const manualTotal = hasManual ? manualSubtotal + Math.round(manualSubtotal * TAX_RATE) : null;

    return (
        <div
            className="space-y-2 border border-zinc-900 rounded-md p-3"
            data-testid={`block-package-row-${idx}`}
        >
            <div className="grid grid-cols-12 gap-2 items-end">
                <div className="col-span-3 sm:col-span-2">
                    <Field label="Sessions">
                        <input
                            type="number"
                            min="1"
                            step="1"
                            value={pkg.sessions ?? ""}
                            onChange={(e) => {
                                // Allow empty string while typing so the coach can
                                // clear the field and type a new number (e.g. 5, 20).
                                // Coerce to min=1 on blur, not while typing.
                                const raw = e.target.value;
                                if (raw === "") {
                                    onChange({ sessions: "" });
                                    return;
                                }
                                const n = parseInt(raw, 10);
                                if (Number.isNaN(n)) return;
                                onChange({ sessions: n });
                            }}
                            onBlur={(e) => {
                                const n = parseInt(e.target.value, 10);
                                onChange({ sessions: Number.isNaN(n) || n < 1 ? 1 : n });
                            }}
                            className={inp}
                            data-testid={`block-pkg-sessions-${idx}`}
                        />
                    </Field>
                </div>
                <div className="col-span-4 sm:col-span-3">
                    <Field label="Auto-renew price ($/sess)">
                        <MoneyInput
                            testid={`block-pkg-price-${idx}`}
                            valueCents={pkg.per_session_cents}
                            onChangeCents={(c) => onChange({ per_session_cents: c })}
                            className={inp}
                        />
                    </Field>
                </div>
                <div className="col-span-4 sm:col-span-3">
                    <Field label="Manual price ($/sess)">
                        {hasManual ? (
                            <div className="flex gap-1">
                                <MoneyInput
                                    testid={`block-pkg-manual-price-${idx}`}
                                    valueCents={pkg.manual_per_session_cents}
                                    onChangeCents={(c) => onChange({ manual_per_session_cents: c })}
                                    className={inp}
                                />
                                <button
                                    type="button"
                                    onClick={() => onChange({ manual_per_session_cents: null })}
                                    className="text-zinc-500 hover:text-brand-red text-[10px] px-1"
                                    title="Clear — use Auto price for both"
                                    data-testid={`block-pkg-manual-clear-${idx}`}
                                >
                                    ×
                                </button>
                            </div>
                        ) : (
                            <button
                                type="button"
                                onClick={() => onChange({ manual_per_session_cents: pkg.per_session_cents })}
                                className="w-full bg-black border border-dashed border-zinc-700 rounded px-2.5 py-1.5 text-[10px] text-zinc-500 hover:border-brand-red hover:text-brand-red text-left"
                                data-testid={`block-pkg-manual-add-${idx}`}
                            >
                                + Add manual price
                            </button>
                        )}
                    </Field>
                </div>
                <div className="col-span-1 pb-1 flex justify-end">
                    <IconBtn onClick={onRemove} danger title="Remove" testid={`block-pkg-remove-${idx}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                    </IconBtn>
                </div>
            </div>
            <div className="text-[10px] text-zinc-500 font-mono pl-1">
                <div>
                    <span className="text-zinc-300">Auto-renew:</span>{" "}
                    {fmtMoney(subtotal)} + {fmtMoney(tax)} tax = <span className="text-white">{fmtMoney(total)}</span>
                </div>
                {hasManual && (
                    <div>
                        <span className="text-zinc-300">Manual:</span>{" "}
                        {fmtMoney(manualSubtotal)} + {fmtMoney(manualTotal - manualSubtotal)} tax = <span className="text-white">{fmtMoney(manualTotal)}</span>
                    </div>
                )}
            </div>
        </div>
    );
}


function SubPreview({ sub }) {
    const total = sub.price_cents * sub.commitment_count;
    const taxRecurring = Math.round(sub.price_cents * TAX_RATE);
    const taxUpfront = Math.round(total * TAX_RATE);
    return (
        <div className="bg-black/40 border border-zinc-800 rounded p-3 text-[11px] font-mono text-zinc-400">
            {sub.allow_recurring && (
                <div>
                    Auto-bill: {fmtMoney(sub.price_cents)} + {fmtMoney(taxRecurring)} tax = <span className="text-white">{fmtMoney(sub.price_cents + taxRecurring)}</span>
                    {" "}/ {intervalShort(sub.interval)} × {sub.commitment_count} {intervalUnit(sub.interval)}{sub.commitment_count === 1 ? "" : "s"}
                </div>
            )}
            {sub.allow_upfront && (
                <div>
                    Upfront: {fmtMoney(total)} + {fmtMoney(taxUpfront)} tax = <span className="text-white">{fmtMoney(total + taxUpfront)}</span> charged once
                </div>
            )}
            {!sub.allow_recurring && !sub.allow_upfront && (
                <div className="text-amber-400 inline-flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" /> No billing option enabled — turn on at least one.
                </div>
            )}
        </div>
    );
}


// =============== Read-only view ===============

function BlockReadOnly({ data }) {
    const sub = data.subscription;
    return (
        <div className="space-y-4">
            {data.description && (
                <p className="text-sm text-zinc-300">{data.description}</p>
            )}
            {sub?.enabled ? (
                <div>
                    <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 inline-flex items-center gap-1">
                        <Repeat className="w-3 h-3" /> Subscription
                    </div>
                    <div className="text-sm">
                        <span className="text-white font-mono">{fmtMoney(sub.price_cents)}</span>
                        <span className="text-zinc-400"> / {intervalShort(sub.interval)} for {sub.commitment_count} {intervalUnit(sub.interval)}{sub.commitment_count === 1 ? "" : "s"}</span>
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-1">
                        {sub.allow_recurring && "Auto-bill"}{sub.allow_recurring && sub.allow_upfront && " · "}{sub.allow_upfront && "Pay upfront"}
                    </div>
                </div>
            ) : (
                <div className="text-xs text-zinc-500 italic">No subscription on this block.</div>
            )}

            <div>
                <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400 mb-2 inline-flex items-center gap-1">
                    <Package className="w-3 h-3" /> Packages
                </div>
                {data.packages.length === 0 ? (
                    <div className="text-xs text-zinc-500 italic">No packages.</div>
                ) : (
                    <ul className="space-y-2 text-sm">
                        {data.packages.map((p) => {
                            const autoSubtotal = p.sessions * p.per_session_cents;
                            const autoTotal = autoSubtotal + Math.round(autoSubtotal * TAX_RATE);
                            const hasManual =
                                p.manual_per_session_cents != null &&
                                p.manual_per_session_cents !== p.per_session_cents;
                            const manualSubtotal = hasManual ? p.sessions * p.manual_per_session_cents : 0;
                            const manualTotal = hasManual ? manualSubtotal + Math.round(manualSubtotal * TAX_RATE) : 0;
                            return (
                                <li key={p.package_id} className="font-mono text-xs">
                                    <div className="flex justify-between items-baseline">
                                        <span className="text-zinc-200 font-semibold">
                                            {p.sessions}-session pack
                                        </span>
                                        {!hasManual && (
                                            <span className="text-zinc-500">
                                                {fmtMoney(autoSubtotal)} <span className="text-zinc-700">+ tax</span> <span className="text-white ml-1">{fmtMoney(autoTotal)}</span>
                                            </span>
                                        )}
                                    </div>
                                    {hasManual ? (
                                        <div className="mt-1 space-y-0.5 pl-3 border-l border-zinc-800">
                                            <div className="flex justify-between text-[11px]">
                                                <span className="text-emerald-300">🔁 Auto-renew · ${(p.per_session_cents / 100).toFixed(0)}/sess</span>
                                                <span className="text-zinc-500">{fmtMoney(autoSubtotal)} <span className="text-zinc-700">+ tax</span> <span className="text-white ml-1">{fmtMoney(autoTotal)}</span></span>
                                            </div>
                                            <div className="flex justify-between text-[11px]">
                                                <span className="text-zinc-400">Manual · ${(p.manual_per_session_cents / 100).toFixed(0)}/sess</span>
                                                <span className="text-zinc-500">{fmtMoney(manualSubtotal)} <span className="text-zinc-700">+ tax</span> <span className="text-white ml-1">{fmtMoney(manualTotal)}</span></span>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-[10px] text-zinc-500 mt-0.5">
                                            ${(p.per_session_cents / 100).toFixed(2)}/sess
                                            {p.auto_renew_default !== false && (
                                                <span className="text-emerald-400 ml-1.5">· 🔁 Auto-renew</span>
                                            )}
                                        </div>
                                    )}
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="text-[10px] text-zinc-500 flex flex-wrap gap-3">
                {data.rollover_sessions && <span>· Sessions roll over on renewal</span>}
                {data.is_fallback && <span className="text-brand-red">· Fallback rollover block</span>}
                {data.show_in_pricing_email && <span className="text-emerald-400">· Shown in pricing email</span>}
                {data.reactivation_fee_cents > 0 && (
                    <span className="text-amber-400">· Reactivation fee: {fmtMoney(data.reactivation_fee_cents)}</span>
                )}
            </div>
        </div>
    );
}


// =============== Helpers + small UI primitives ===============

const inp = "w-full bg-black border border-zinc-700 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:border-brand-red";

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-400 block mb-1">{label}</span>
            {children}
        </label>
    );
}

function Toggle({ on, onChange, label, hint, danger, compact, testid }) {
    return (
        <button
            type="button"
            onClick={() => onChange(!on)}
            data-testid={testid}
            className={`inline-flex items-start gap-2 text-left ${compact ? "" : "py-1"}`}
        >
            <span
                className={`mt-0.5 w-9 h-5 rounded-full border transition-colors flex-shrink-0 relative ${
                    on
                        ? danger
                            ? "bg-brand-red border-brand-red"
                            : "bg-emerald-600 border-emerald-600"
                        : "bg-zinc-900 border-zinc-700"
                }`}
            >
                <span
                    className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${
                        on ? "translate-x-[18px]" : "translate-x-0.5"
                    }`}
                />
            </span>
            <span className="block">
                <span className="text-xs text-white block">{label}</span>
                {hint && <span className="text-[10px] text-zinc-500 block mt-0.5 max-w-xs">{hint}</span>}
            </span>
        </button>
    );
}

function IconBtn({ onClick, children, title, danger, testid }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            data-testid={testid}
            className={`p-1.5 rounded hover:bg-zinc-800 transition-colors ${
                danger ? "text-zinc-400 hover:text-brand-red" : "text-zinc-300 hover:text-white"
            }`}
        >
            {children}
        </button>
    );
}

function intervalShort(interval) {
    return { daily: "day", weekly: "wk", monthly: "mo" }[interval] || interval;
}
function intervalUnit(interval) {
    return { daily: "day", weekly: "week", monthly: "month" }[interval] || interval;
}

function defaultSub() {
    return {
        enabled: true,
        price_cents: 1200,
        interval: "weekly",
        commitment_count: 4,
        allow_recurring: true,
        allow_upfront: true,
        stripe_price_id: null,
    };
}

function deepClone(obj) {
    return JSON.parse(JSON.stringify(obj));
}

function serializeForApi(draft) {
    const out = {
        name: draft.name?.trim() || "",
        description: draft.description?.trim() || "",
        active: !!draft.active,
        is_fallback: !!draft.is_fallback,
        rollover_sessions: !!draft.rollover_sessions,
        show_in_pricing_email: !!draft.show_in_pricing_email,
        reactivation_fee_cents: parseInt(draft.reactivation_fee_cents) || 0,
        packages: (draft.packages || []).map((p) => ({
            package_id: p.package_id || null,
            sessions: parseInt(p.sessions) || 1,
            per_session_cents: parseInt(p.per_session_cents) || 0,
            manual_per_session_cents:
                p.manual_per_session_cents == null
                    ? null
                    : (parseInt(p.manual_per_session_cents) || 0),
            auto_renew_default: p.auto_renew_default !== false,
            // Label is auto-derived server-side from sessions + price. Don't
            // send a stale stored label — that's how "save didn't save"
            // appeared (description showed old price even though input + math
            // had the new one).
            label: "",
        })),
        subscription: null,
    };
    if (draft.subscription?.enabled) {
        out.subscription = {
            enabled: true,
            price_cents: parseInt(draft.subscription.price_cents) || 0,
            interval: draft.subscription.interval || "weekly",
            commitment_count: parseInt(draft.subscription.commitment_count) || 1,
            allow_recurring: !!draft.subscription.allow_recurring,
            allow_upfront: !!draft.subscription.allow_upfront,
        };
    }
    return out;
}
